import { ArrowRight, BarChart, Handshake, Coins, Building2, Settings, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ServiceCard from '../components/ServiceCard';
import ConsultantCard from '../components/ConsultantCard';
import { useCMS, pickLoc, consultantField, activeConsultants } from '../context/CMSContext';
import { useSeo } from '../lib/seo';
import { resolveConsultantImage } from '../lib/images';
import heroBg from '../assets/hero-bg.jpg';

/** Normalise an i18n value that may be an array of points (new) or a plain
 *  string (legacy data) into a renderable list. */
function toList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

/** Turn a phone number inside text into a tappable tel: link. */
function withTelLink(text: string) {
  const m = text.match(/(\+?852[\s-]?\d{4}[\s-]?\d{4})/);
  if (!m) return text;
  const tel = m[1].replace(/[\s-]/g, '');
  const [before, after] = text.split(m[1]);
  return (
    <>
      {before}
      <a
        href={`tel:${tel}`}
        className="text-gold-600 font-semibold hover:text-gold-700 transition-colors whitespace-nowrap"
      >
        {m[1]}
      </a>
      {after}
    </>
  );
}

const Home = () => {
  const { t } = useTranslation();
  const { cms, lang } = useCMS();

  const seo = cms.seo.pages.home;

  useSeo({
    title: pickLoc(seo.title, lang),
    description: pickLoc(seo.description, lang),
    keywords: pickLoc(seo.keywords, lang),
    path: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: `${cms.site.orgNameTc} ${cms.site.orgNameEn}`,
      inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
      publisher: {
        '@type': 'ProfessionalService',
        name: cms.site.orgNameTc,
        alternateName: cms.site.orgNameEn,
      },
    },
  });

  const consultants = activeConsultants(cms).slice(0, 4);

  const services = [
    {
      id: 'ipo',
      icon: <BarChart className="h-8 w-8" />,
      link: '/services'
    },
    {
      id: 'ma',
      icon: <Handshake className="h-8 w-8" />,
      link: '/services'
    },
    {
      id: 'corpFin',
      icon: <Coins className="h-8 w-8" />,
      link: '/services'
    },
    {
      id: 'corpStruct',
      icon: <Building2 className="h-8 w-8" />,
      link: '/services'
    },
    {
      id: 'opsMgmt',
      icon: <Settings className="h-8 w-8" />,
      link: '/services'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBg}
            alt="Corporate Office"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 via-navy-800/80 to-teal-900/40"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6 font-serif">
              {t('hero.title')}
            </h1>
            <p className="text-xl md:text-2xl text-gold-400 mb-8 font-light tracking-wide">
              {t('hero.subtitle')}
            </p>
            <Link
              to="/contact"
              className="inline-block px-8 py-4 bg-gold-500 hover:bg-gold-600 text-navy-900 font-bold rounded transition-colors text-center shadow-lg hover:shadow-gold-500/20"
            >
              {t('hero.cta')}
            </Link>
          </div>
        </div>
      </section>

      {/* Direct-answer intro (GEO: citable summary within the first paragraph) */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {(() => {
            const label = t('homeIntro.label');
            const parts = String(t('homeIntro.body') || '')
              .split('\n')
              .map((s) => s.trim())
              .filter(Boolean);
            return (
              <>
                {label && label !== 'homeIntro.label' && (
                  <p className="text-xs font-semibold tracking-[0.35em] uppercase text-gold-600 mb-5">
                    {label}
                  </p>
                )}
                {parts.map((part, i) => {
                  const isFirst = i === 0;
                  const isLast = i === parts.length - 1;
                  if (isFirst) {
                    return (
                      <p key={i} className="font-serif text-xl md:text-2xl text-navy-900 leading-relaxed mb-6">
                        {part}
                      </p>
                    );
                  }
                  if (isLast && parts.length > 1) {
                    return (
                      <p key={i} className="text-lg text-navy-800 leading-relaxed border-l-4 border-gold-500 pl-5">
                        {withTelLink(part)}
                      </p>
                    );
                  }
                  return (
                    <p key={i} className="text-lg text-gray-600 leading-loose mb-6">
                      {part}
                    </p>
                  );
                })}
                <p className="text-sm text-gray-400 text-center mt-6">
                  <span rel="author">{t('homeIntro.byline')}</span> ·{' '}
                  <a href="/about" className="text-gold-600 hover:text-gold-700 underline-offset-2 hover:underline">
                    {t('nav.about')}
                  </a>
                </p>
              </>
            );
          })()}
        </div>
      </section>

      {/* Vision & Mission (bullet form for humans + AI agents) */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-6 font-serif border-l-4 border-gold-500 pl-4">
                {t('visionMission.visionTitle')}
              </h2>
              <ul className="space-y-4">
                {toList(t('visionMission.visionDesc', { returnObjects: true })).map((pt, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-2.5 h-2 w-2 rounded-full bg-gold-500 flex-shrink-0 mr-4" aria-hidden="true"></span>
                    <span className="text-lg text-gray-600 leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-6 font-serif border-l-4 border-teal-500 pl-4">
                {t('visionMission.missionTitle')}
              </h2>
              <ul className="space-y-4">
                {toList(t('visionMission.missionDesc', { returnObjects: true })).map((pt, i) => (
                  <li key={i} className="flex items-start">
                    <span className="mt-2.5 h-2 w-2 rounded-full bg-teal-500 flex-shrink-0 mr-4" aria-hidden="true"></span>
                    <span className="text-lg text-gray-600 leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-navy-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4 font-serif">{t('services.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                title={t(`services.${service.id}.title`)}
                tagline={t(`services.${service.id}.tagline`)}
                description={t(`services.${service.id}.desc`).substring(0, 100) + '...'}
                link={service.link}
                icon={service.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center font-serif">{t('whyChooseUs.title')}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(t('whyChooseUs.items', { returnObjects: true }) as any[]).map((item: any, index: number) => (
              <div key={index} className="flex items-start p-6 bg-navy-800/50 rounded-lg border border-navy-700 hover:border-gold-500/50 transition-colors">
                <CheckCircle className="h-6 w-6 text-gold-500 flex-shrink-0 mt-1" />
                <div className="ml-4">
                  <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories (GEO: citable, data-rich results) */}
      {(() => {
        const stories = t('successStories', { returnObjects: true }) as any;
        if (!stories || stories.enabled === 'false' || !Array.isArray(stories.cases) || stories.cases.length === 0) return null;
        return (
          <section className="py-20 bg-navy-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4 font-serif">{stories.title}</h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">{stories.desc}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {stories.cases.map((c: any, index: number) => (
                  <div key={index} className="bg-white rounded-lg shadow-md p-8 border-t-4 border-gold-500 flex flex-col">
                    <h3 className="text-lg font-bold text-navy-900 mb-3 font-serif">{c.title}</h3>
                    <p className="text-sm text-teal-600 font-medium uppercase tracking-wide mb-4">{c.company}</p>
                    <p className="text-gray-600 leading-relaxed">{c.result}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Consultant Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4 font-serif">{t('team.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('team.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {consultants.map((consultant) => (
              <ConsultantCard
                key={consultant.id}
                id={consultant.id}
                name={consultantField(consultant, 'name', lang)}
                role={consultantField(consultant, 'title', lang)}
                tags=""
                bio={consultantField(consultant, 'description', lang)}
                image={resolveConsultantImage(consultant.imageUri)}
                ctaText={t('team.cta')}
              />
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/consultants" className="inline-flex items-center text-navy-600 font-bold hover:text-navy-800 transition-colors">
              {t('common.viewAllConsultants')} <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-teal-900 to-navy-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">{t('cta.title')}</h2>
          <p className="text-xl text-gray-200 mb-10">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/contact"
              className="px-8 py-4 bg-gold-500 hover:bg-gold-600 text-navy-900 font-bold rounded transition-colors"
            >
              {t('cta.primary')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
